import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { Permission, PlanFeature } from '@makila/shared';
import { api, post, refreshSession, setAccessToken, setOnSessionLost } from './api';
import { type HostKind, resolveHost } from './tenant-host';

export interface Session {
  user: { id: string; name: string; email: string; phone?: string | null; totp_enabled: boolean; is_platform_admin: boolean; email_verified_at: string | null };
  tenant: { id: string; slug: string; legal_name: string; trade_name: string | null; nuit: string; status: string; training_mode: boolean; trial_ends_at: string | null; paid_until: string | null; plan_code: string; default_payment_method: string | null; default_payment_terms_days: number; quote_validity_days: number; vat_regime: string; plan_name: string; features: Record<string, boolean> | null; nuit_verified_at: string | null; email: string | null };
  companies: { id: string; slug: string; legal_name: string; role: string }[];
  /** Estado efectivo da subscrição (calculado pela API a partir das datas). */
  subscription: { status: 'trial' | 'active' | 'past_due' | 'read_only' | 'suspended' | 'closed'; writable: boolean; until: string | null; daysLeft: number | null; reason: string | null };
  role: string;
  permissions: Permission[];
  today: string;
  /** Entrada com credenciais desta sessão (a consola exige-a recente e com 2FA). */
  auth: { at: string; mfa: boolean } | null;
  /** Dono/administrador sem 2FA: a partir desta data as acções sensíveis exigem-na (null = não se aplica). */
  mfaDeadline: string | null;
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  /** Funcionalidade incluída no plano da empresa. */
  has: (f: PlanFeature) => boolean;
  login: (email: string, password: string, second?: { totp?: string; recoveryCode?: string }) => Promise<void>;
  signup: (body: unknown) => Promise<Session>;
  logout: () => Promise<void>;
  switchTenant: (id: string) => Promise<void>;
  reload: () => Promise<void>;
  /** Tipo de host: portal (apex), admin, ou empresa. */
  hostKind: HostKind;
  /** Slug da empresa no subdomínio actual (null no portal/admin). */
  hostSlug: string | null;
  /** Autenticado num subdomínio de empresa a que esta conta não pertence. */
  notMemberOfHost: boolean;
}

const Ctx = createContext<AuthState | null>(null);

/** Módulos que dependem do plano: sem a funcionalidade, as permissões correspondentes não valem. */
export const featureOf = (p: Permission): PlanFeature[] => (p.startsWith('stock:') ? ['stock'] : p.startsWith('purchases:') ? ['purchases'] : []);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hostKind, setHostKind] = useState<HostKind>('company');
  const [hostSlug, setHostSlug] = useState<string | null>(null);
  const [notMemberOfHost, setNotMemberOfHost] = useState(false);

  const accept = useCallback((r: { accessToken: string; session: Session }) => { setAccessToken(r.accessToken); setSession(r.session); }, []);
  const reload = useCallback(async () => { const r = await api<{ session: Session }>('GET', '/auth/me'); setSession(r.session); }, []);
  const switchTenant = useCallback(async (tenantId: string) => accept(await post('/auth/switch-tenant', { tenantId }) as never), [accept]);

  useEffect(() => {
    setOnSessionLost(() => { setAccessToken(null); setSession(null); });
    // Ao abrir a página: qual a empresa deste subdomínio? E há sessão?
    (async () => {
      try { const h = await resolveHost(); setHostKind(h.kind); setHostSlug(h.slug); } catch { /* domínio-base desconhecido */ }
      if (await refreshSession()) { try { await reload(); } catch { /* sem sessão */ } }
      setLoading(false);
    })();
  }, [reload]);

  // Num subdomínio de empresa, a empresa activa da sessão deve ser a do host.
  // Se o utilizador pertence a ela mas tem outra activa, troca; se não pertence,
  // marca o desacordo (a UI mostra um aviso com saída para o domínio principal).
  useEffect(() => {
    if (hostKind !== 'company' || !session || !hostSlug) { setNotMemberOfHost(false); return; }
    if (session.tenant.slug === hostSlug) { setNotMemberOfHost(false); return; }
    const match = session.companies.find((c) => c.slug === hostSlug);
    if (match) { switchTenant(match.id).catch(() => {}); } else { setNotMemberOfHost(true); }
    // Ao mudar session.tenant.slug este efeito corre de novo e pára quando casar.
  }, [hostKind, session, hostSlug, switchTenant]);

  const value: AuthState = {
    session, loading,
    // Em só leitura (subscrição), só consultar e exportar: os botões de escrita desaparecem.
    can: (p) => !!session?.permissions.includes(p) && (session.subscription.writable || p.endsWith(':view') || p.endsWith(':export') || p.startsWith('billing:')) && featureOf(p).every((f) => !!session.tenant.features?.[f]),
    has: (f) => !!session?.tenant.features?.[f],
    login: async (email, password, second) => accept(await post('/auth/login', {
      email, password, ...(second?.totp ? { totp: second.totp } : {}), ...(second?.recoveryCode ? { recoveryCode: second.recoveryCode } : {}),
    }) as never),
    signup: async (body) => { const r = await post('/auth/signup', body) as { accessToken: string; session: Session }; accept(r); return r.session; },
    logout: async () => { await post('/auth/logout').catch(() => {}); setAccessToken(null); setSession(null); },
    switchTenant,
    reload,
    hostKind,
    hostSlug,
    notMemberOfHost,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fora do AuthProvider');
  return v;
}
